import { Dialog } from "primereact/dialog"
import { Button } from "primereact/button"
import { Calendar } from "primereact/calendar"
import { Checkbox } from "primereact/checkbox"
import { InputTextarea } from "primereact/inputtextarea"
import kioskStyles from "../../../../styles/kiosk/KioskAdmin.module.css"
import styles from "../../../../styles/kiosk/KioskParticipantManagement.module.css"
import { useFormik } from "formik"
import { getFormErrorMessage, isFormFieldValid } from "../helpers/helpers"
import { useContext, useEffect } from "react"
import { UserContext } from "../../../../context/userContext"
import { LogError } from "../../../../helpers/logger"
import { kioskApiPostParticipantsLock } from "../../kioskApi"
import { DateTime } from "luxon"

const LockModal = ({
  show,
  setShow,
  selectedParticipant,
  setSelectedParticipant,
  kiosk_organization_id,
  refreshList,
}) => {
  const userCtx = useContext(UserContext)

  const formik = useFormik({
    initialValues: {
      lockExpiryDate: new Date(),
      notExpire: false,
      lockMessage: "Your profile is currently locked",
    },
    validate: (data) => {
      let errors = {}

      //Only validate if a participant is going to be locked
      if (!selectedParticipant?.status) {
        if (!data.lockMessage) {
          errors.lockMessage = "This field is required"
        }
        if (!data.lockExpiryDate) {
          if (!data.notExpire) {
            errors.lockExpiryDate = "This field is required"
          }
        }
      }

      return errors
    },
    onSubmit: (data) => {
      lockParticipant(data)
    },
  })
  useEffect(() => {
    if (selectedParticipant) {
      const participantLockDate = DateTime.fromISO(
        selectedParticipant.lockExpiry,
        {
          zone: "utc",
        }
      ).toLocal()
      formik.setFieldValue("notExpire", selectedParticipant.lockExpiry)
      const newDate = new Date()
      formik.setFieldValue(
        "lockExpiryDate",
        selectedParticipant.lockExpiry
          ? new Date(participantLockDate)
          : new Date(newDate.setDate(newDate.getDate() + 1))
      )
      formik.setFieldValue("lockMessage", selectedParticipant.lockMessage)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedParticipant])

  const lockParticipant = async (data) => {
    try {
      const payload = {
        ...data,
        organizationId: kiosk_organization_id,
        participantId: selectedParticipant?.participantId,
        participantMembershipId: selectedParticipant?.participantMembershipId,
        locked: !selectedParticipant?.status,
      }
      await kioskApiPostParticipantsLock(userCtx.tokenKiosk, payload)
      clean()
      refreshList(true)
    } catch (error) {
      LogError("Unable to apply participant lock configuration values", error)
      userCtx.setNotificationError(
        "Unable to apply participant lock configuration values"
      )
      clean()
    }
  }
  const clean = () => {
    formik.resetForm()
    formik.setSubmitting(false)
    setSelectedParticipant(null)
    setShow(false)
  }
  const headerText = () => {
    return <label>Participant Lock Setting</label>
  }
  const submitButtonText = () => {
    if (selectedParticipant?.status) {
      return "Unlock Profile"
    }
    return "Lock Profile"
  }
  return (
    <Dialog visible={show} showHeader={false} style={{ width: "40vw" }}>
      <div className={kioskStyles.modalHeader}>
        {headerText()}
        <Button
          icon="pi pi-times"
          className={kioskStyles.hideModalButton}
          onClick={() => {
            clean()
          }}
          disabled={formik.isSubmitting}
        />
      </div>
      <div className={kioskStyles.modalFormContainer}>
        <hr className={kioskStyles.modalHeaderLine} />
        <form onSubmit={formik.handleSubmit}>
          <div className={kioskStyles.modalField}>
            <label className={kioskStyles.modalFieldLabel}>
              Lock Expiry Date <i className="pi pi-info-circle"></i>
            </label>
            <Calendar
              showIcon
              name="lockExpiryDate"
              onChange={formik.handleChange}
              className={`${kioskStyles.calendarInput}`}
              inputClassName={`${kioskStyles.calendarInput} ${
                isFormFieldValid(formik, "lockExpiryDate") &&
                kioskStyles.calendarInputError
              }`}
              disabled={formik.values.notExpire || formik.isSubmitting}
              value={
                formik.values.notExpire
                  ? undefined
                  : formik.values.lockExpiryDate
              }
              minDate={new Date()}
            />
            {getFormErrorMessage(formik, "lockExpiryDate")}
            <div className={styles.checkboxContainer}>
              <Checkbox
                id="notExpire"
                name="notExpire"
                onChange={formik.handleChange}
                checked={formik.values.notExpire}
                className={kioskStyles.checkboxInput}
                disabled={formik.isSubmitting}
              />
              <label htmlFor="notExpire" className="ml-2">
                Lock does not expire
              </label>
            </div>
          </div>
          <div className={kioskStyles.modalField}>
            <label className={kioskStyles.modalFieldLabel}>
              Locked Message * <i className="pi pi-info-circle"></i>
            </label>
            <InputTextarea
              style={{ width: "100%" }}
              autoResize
              name="lockMessage"
              value={formik.values.lockMessage}
              onChange={formik.handleChange}
              rows={5}
              cols={30}
              placeholder="Your profile is currently locked"
              className={`${styles.lockedMessageInput} ${
                isFormFieldValid(formik, "lockMessage") &&
                kioskStyles.inputError
              }`}
              disabled={formik.isSubmitting}
            />
            {getFormErrorMessage(formik, "lockMessage")}
          </div>
          <div className={kioskStyles.modalFooter}>
            <Button
              label="Cancel"
              className={`${kioskStyles.modalFooterButton} ${kioskStyles.modalCancelButton}`}
              onClick={() => {
                clean()
              }}
              disabled={formik.isSubmitting}
              type="button"
            />
            <Button
              icon={formik.isSubmitting && "pi pi-spin pi-spinner"}
              label={submitButtonText()}
              type="submit"
              disabled={formik.isSubmitting}
              className={kioskStyles.modalFooterButton}
            />
          </div>
        </form>
      </div>
    </Dialog>
  )
}
export default LockModal
